import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { adminApi } from '../lib/api.ts';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { CompanyStatusBadge } from '../components/StatusBadge.tsx';
import { LinkBtn } from '../components/LinkBtn.tsx';
import type { Company, CompanyWithCounts } from '../lib/types.ts';
import type { CompanyStatus } from '@l2u/shared/schemas';

const STATUSES: CompanyStatus[] = ['invited', 'in_progress', 'review', 'changes', 'compliant', 'expired'];

export function AdminDashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: () => adminApi.get<{ companies: Company[] }>('/api/companies'),
  });
  const queueQuery = useQuery({
    queryKey: ['review-queue'],
    queryFn: () =>
      adminApi.get<{ companies: CompanyWithCounts[] }>('/api/companies/review/queue'),
  });

  if (isLoading) return <CenteredSpinner />;
  const companies = data?.companies ?? [];
  const queue = queueQuery.data?.companies ?? [];

  const counts: Record<CompanyStatus, number> = {
    invited: 0, in_progress: 0, review: 0, changes: 0, compliant: 0, expired: 0,
  };
  for (const c of companies) counts[c.status] += 1;

  const submissionsToAction = queue.reduce(
    (n, c) => n + (c.submission_counts?.pending ?? 0) + (c.submission_counts?.rejected ?? 0),
    0,
  );

  const recent = [...companies]
    .sort((a, b) => (b.updated_at < a.updated_at ? -1 : 1))
    .slice(0, 6);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Dashboard</h1>
        <p className="mt-1 text-sm text-slate-500">
          {companies.length} compan{companies.length === 1 ? 'y' : 'ies'} tracked
        </p>
      </div>

      {queue.length > 0 && (
        <div className="flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div>
            <div className="text-sm font-semibold text-amber-900">
              {queue.length} compan{queue.length === 1 ? 'y' : 'ies'} awaiting review
            </div>
            <div className="text-xs text-amber-800">
              {submissionsToAction} submission{submissionsToAction === 1 ? '' : 's'} to action
            </div>
          </div>
          <LinkBtn variant="secondary" size="sm" to="/admin/reviews">Open queue →</LinkBtn>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        {STATUSES.map((s) => (
          <div key={s} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
              {labelFor(s)}
            </div>
            <div className="mt-2 text-2xl font-semibold text-slate-900">{counts[s]}</div>
          </div>
        ))}
      </div>

      <div>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
          Recent activity
        </h2>
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2">Company</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Progress</th>
                <th className="px-4 py-2">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {recent.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2">
                    <Link to={`/admin/companies/${c.id}`} className="font-medium text-slate-900 hover:underline">
                      {c.company_name}
                    </Link>
                    <div className="text-xs text-slate-500">{c.contact_name}</div>
                  </td>
                  <td className="px-4 py-2"><CompanyStatusBadge status={c.status} /></td>
                  <td className="px-4 py-2 text-slate-700">{c.progress}%</td>
                  <td className="px-4 py-2 text-xs text-slate-500">
                    {new Date(c.updated_at).toLocaleString()}
                  </td>
                </tr>
              ))}
              {recent.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-slate-500">No companies yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function labelFor(s: CompanyStatus): string {
  switch (s) {
    case 'invited': return 'Invited';
    case 'in_progress': return 'In progress';
    case 'review': return 'In review';
    case 'changes': return 'Changes';
    case 'compliant': return 'Compliant';
    case 'expired': return 'Expired';
  }
}
